import React, { useState, memo } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { User, Heart, Users, Shield, Brain, ArrowRight, CheckCircle2, Calendar, ChevronDown } from 'lucide-react';
const SERVICE_DATA: Record<string, {
  title: string;
  tagline: string;
  icon: React.ElementType;
  image: string;
  whyDescription: string;
  supportAreas: string[];
  longDescription: string;
  approaches: string[];
  faqs: {
    question: string;
    answer: string;
  }[];
  customSection1Title?: string;
  customSection2Title?: string;
  customSection3Title?: string;
  customSection2Image?: string;
  customSection3Image?: string;
}> = {
  individual: {
    title: 'Individual Counselling & Therapy',
    tagline: 'A safe, confidential space to explore, heal, and grow',
    icon: User,
    image: 'https://cdn.magicpatterns.com/uploads/aXDkFi9YTgLse5FpNbH4ZG/white_girl_individual.single.webp',
    customSection2Image: 'https://cdn.magicpatterns.com/uploads/jYYgBpEzRjwaHBTsjhQd17/black_guy_individual_therapy.webp',
    customSection3Image: 'https://cdn.magicpatterns.com/uploads/6v5CAyhKGx6YWFhGaYhTxT/image.webp',
    whyDescription: "Individual therapy offers you a dedicated, confidential space to explore your thoughts, feelings, and experiences without judgment. It's an opportunity to work one-on-one with a trained therapist who can help you understand yourself better, develop effective coping strategies, and create meaningful change in your life.",
    supportAreas: ['Those experiencing anxiety, depression, or overwhelming stress', 'Individuals processing grief, loss, or major life transitions', 'People seeking to improve self-esteem and personal identity', 'Anyone wanting to develop healthier coping strategies', 'Those dealing with PTSD or trauma-related symptoms', 'Individuals navigating cultural identity and belonging'],
    longDescription: "Together, we work at a pace that feels safe and respectful, focusing on what you're facing right now while gently exploring deeper emotions and past experiences that may be shaping your present struggles.\n\nThrough this process, we build awareness, emotional safety, and new ways of relating to yourself and others that support lasting change.\n\nUsing Emotionally Focused Therapy (EFT), we support emotional awareness, self-compassion, and healthier relationships — so you don't have to do it alone.",
    approaches: ['Cognitive Behavioural Therapy (CBT)', 'EMDR', 'Emotionally Focused Therapy (EFT)', 'Gottman Method', 'Trauma-Informed Care', 'Solution-Focused Therapy'],
    faqs: [{
      question: 'How long are individual therapy sessions?',
      answer: 'Individual sessions are 50 minutes. The frequency of sessions depends on your needs — most clients start with weekly sessions and adjust from there.'
    }, {
      question: 'Do I need a referral for individual therapy?',
      answer: 'No referral is needed. You can book directly through our website or by calling the office.'
    }, {
      question: 'How many sessions will I need?',
      answer: "This varies depending on your goals and concerns. Some clients see improvement in 6-8 sessions, while others benefit from longer-term support. We'll discuss this during your initial consultation."
    }, {
      question: 'Can I switch between in-person and virtual sessions?',
      answer: 'Absolutely. Many clients alternate between in-person and virtual sessions based on their schedule and preference.'
    }]
  },
  couples: {
    title: 'Couples Therapy',
    tagline: 'Rebuild connection, improve communication, and strengthen your bond',
    icon: Heart,
    image: 'https://cdn.magicpatterns.com/uploads/9R2jH4G5J9CdxwBvXNdhpd/Why_Couples_Therapy.png',
    customSection2Image: 'https://cdn.magicpatterns.com/uploads/kRt4h1Y8VH63bWw2EDXNp4/black_couple_smiling_..png',
    customSection3Image: 'https://cdn.magicpatterns.com/uploads/3EHutEPypcGgWq5gFfSbsF/em.couples.webp',
    whyDescription: 'Every relationship goes through difficult seasons. Using Emotionally Focused Therapy (EFT) and the Gottman Method, couples therapy provides a structured, neutral space where both partners can be heard and understood. With professional guidance, you can break negative patterns, rebuild trust, and develop the communication skills needed to strengthen your bond.',
    supportAreas: ['Couples experiencing communication breakdowns', 'Partners navigating trust issues or infidelity recovery', 'Those considering separation and wanting clarity', 'Couples preparing for marriage or major life changes', 'Partners wanting to deepen their emotional connection', 'Couples navigating cultural or interfaith differences'],
    longDescription: "Couples therapy offers a safe, supportive space to slow things down, understand these patterns, and reconnect.\n\nTogether, we use Emotionally Focused Therapy (EFT) to explore what's happening beneath the surface, strengthen emotional connection, and help partners feel seen, heard, and supported.\n\nAlso, we use the Gottman Method to build a strong foundation of friendship, trust and emotional intimacy — helping you feel more connected and equipped for a lasting change.",
    approaches: ['Emotionally Focused Therapy (EFT)', 'Gottman Method', 'Attachment-Based Therapy', 'Communication Skills Training', 'Conflict Resolution Techniques'],
    faqs: [{
      question: 'How long are couples therapy sessions?',
      answer: 'Couples sessions are 50 minutes to allow adequate time for both partners to be heard and to practice new skills together.'
    }, {
      question: "What if my partner doesn't want to come?",
      answer: "It's common for one partner to be hesitant. I can work with you individually first, and we can discuss strategies to help your partner feel more comfortable joining."
    }, {
      question: 'Do you take sides in couples therapy?',
      answer: 'No. My role is to be a neutral facilitator. I help both partners feel heard and work together toward shared goals.'
    }, {
      question: 'How often should we attend couples therapy?',
      answer: 'Most couples start with weekly sessions. As you develop new skills and see improvement, we may move to bi-weekly sessions.'
    }]
  },
  family: {
    title: 'Family Therapy',
    tagline: 'Strengthen family bonds and navigate challenges together',
    icon: Users,
    image: 'https://cdn.magicpatterns.com/uploads/41LF6QCZkEn31GAZE67gAa/Why_Family_Therapy.webp',
    customSection2Image: 'https://cdn.magicpatterns.com/uploads/x2k8PB5oyDM5ZkSFWXqAWC/Mixed-family-with-teen-girls-at-park-sq-frame.webp',
    customSection3Image: 'https://cdn.magicpatterns.com/uploads/gMhJkwuWSGEmEnARKVKBxR/My_process_family_therapy..png',
    whyDescription: "Family dynamics shape who we are. When those dynamics become strained, everyone feels the impact. Family therapy creates a safe space for all members to express themselves, understand each other's perspectives, and work together toward healthier patterns of communication and connection.",
    supportAreas: ['Families experiencing conflict or communication breakdown', 'Parents navigating challenges with adolescents', 'Blended families adjusting to new dynamics', "Families dealing with a member's mental health challenges", 'Those working through intergenerational patterns', 'Families navigating cultural transitions or immigration adjustment'],
    longDescription: "Family therapy offers a supportive space to slow down and understand what is happening beneath the surface.\n\nUsing an attachment-based, emotionally focused approach, we work to honour each family member's experience while strengthening emotional safety and connection.\n\nTogether, we explore patterns of interaction, support healing from relational wounds, and help families move toward more open, responsive, and supportive relationships.",
    approaches: ['Emotionally Focused Therapy (EFT)', 'Gottman Method', 'Narrative Therapy', 'Family Systems', 'Parenting Skills Training'],
    faqs: [{
      question: 'Who needs to attend family therapy sessions?',
      answer: "This depends on the issues being addressed. Sometimes the whole family attends, other times specific members. We'll discuss the best approach during the initial consultation."
    }, {
      question: 'What ages do you work with in family therapy?',
      answer: 'I work with families that include members of all ages, from young children to adults. Sessions are adapted to be age-appropriate.'
    }, {
      question: 'How long are family therapy sessions?',
      answer: 'Family sessions are typically 50 minutes, though initial sessions may run longer to allow time for everyone to share.'
    }, {
      question: 'Can family therapy help with parenting challenges?',
      answer: 'Yes, family therapy is very effective for addressing parenting challenges, improving parent-child communication, and developing consistent parenting strategies.'
    }]
  },
  trauma: {
    title: 'Trauma Therapy',
    tagline: 'Safely process traumatic experiences and reclaim your life',
    icon: Shield,
    image: 'https://cdn.magicpatterns.com/uploads/vbdxDHdANHsPtAtNsTgMbN/trauma_therapy_2.png',
    customSection1Title: 'Why Trauma Therapy?',
    customSection2Image: 'https://cdn.magicpatterns.com/uploads/iaSk7PLsVJMGTABVW3eXcz/em.trauma.1.webp',
    customSection3Image: 'https://cdn.magicpatterns.com/uploads/qWWhb9MCiuMfu8E2SVeEwq/em.trauma.webp',
    whyDescription: "Trauma doesn't just live in your memories — it lives in your body, your relationships, and your daily life. Specialized trauma therapy provides the tools and support you need to safely process these experiences, reduce their hold on you, and reclaim your sense of safety and control.",
    supportAreas: ['Individuals with PTSD or complex trauma', 'Survivors of abuse, violence, or neglect', 'Those affected by accidents or natural disasters', 'People experiencing flashbacks, nightmares, or hypervigilance', 'Anyone whose past trauma is affecting their present life', 'First responders and healthcare workers experiencing vicarious trauma'],
    longDescription: 'I offer a calm, steady presence where we attend to both your emotions and how they show up in your body. Our work is grounded in safety, compassion, and an attachment-focused understanding of healing.\n\nTogether, we move at your pace — helping your nervous system settle, easing the intensity of painful memories, and strengthening your sense of safety and resilience, one step at a time.',
    approaches: ['Trauma-Focused CBT', 'EMDR', 'Somatic Experiencing', 'Grounding & Stabilization Techniques'],
    faqs: [{
      question: 'Is trauma therapy safe?',
      answer: "Yes. I use a phased approach that prioritizes your safety and stability. We never move faster than you're comfortable with, and you're always in control of the pace."
    }, {
      question: 'How is trauma therapy different from regular therapy?',
      answer: "Trauma therapy uses specialized techniques designed specifically to process traumatic memories and reduce their emotional impact. It's more targeted than general talk therapy."
    }, {
      question: 'Will I have to relive my trauma?',
      answer: "No. While we may briefly touch on traumatic memories during processing, the goal is to reduce their emotional charge — not to re-traumatize you. You're always in control."
    }, {
      question: 'How long does trauma therapy take?',
      answer: 'This varies significantly depending on the nature and complexity of the trauma. Some clients see improvement in 8-12 sessions, while complex trauma may require longer treatment.'
    }]
  },
  emdr: {
    title: 'EMDR Therapy',
    tagline: 'Reprocess disturbing memories and find lasting relief',
    icon: Brain,
    image: 'https://cdn.magicpatterns.com/uploads/8LUbANKqoMbpwyRXAgLLtU/em.emdr_.1.webp',
    whyDescription: "EMDR is one of the most researched and effective treatments for trauma and PTSD. Unlike traditional talk therapy, EMDR works with your brain's natural healing processes to reprocess disturbing memories, reducing their emotional intensity and helping you move forward without being held back by the past.",
    customSection1Title: 'Why EMDR Therapy?',
    customSection2Title: 'EMDR is highly effective for',
    customSection3Title: 'My Approach',
    customSection2Image: 'https://cdn.magicpatterns.com/uploads/w5PL5XNxHb229fDPVjSHNo/EMDR_THERPAY.webp',
    customSection3Image: 'https://cdn.magicpatterns.com/uploads/4rbk9hW21nntHbm9GQtkSe/My_Approach.webp',
    supportAreas: ['Individuals with PTSD or single-incident trauma', 'Those with anxiety, phobias, or panic attacks', 'People struggling with disturbing memories', 'Individuals with negative self-beliefs rooted in past experiences', "Those who haven't responded to traditional talk therapy", 'First responders dealing with critical incident stress'],
    longDescription: 'As an EMDR Therapist, my approach to EMDR is collaborative, gentle, and paced to your comfort.\n\nWe begin by building trust, teaching coping tools, and ensuring you feel grounded before working with challenging experiences.\n\nYou remain in control throughout the process, and I provide steady, compassionate guidance to help you feel supported, empowered, and confident in your journey toward healing.',
    approaches: ['Phase 1: History Taking & Treatment Planning', 'Phase 2: Preparation', 'Phase 3: Assessment', 'Phase 4: Desensitization', 'Phase 5: Installation', 'Phase 6: Body Scan', 'Phase 7: Closure', 'Phase 8: Reevaluation'],
    faqs: [{
      question: 'What does EMDR feel like?',
      answer: "During EMDR, you'll briefly focus on a traumatic memory while following guided eye movements or tapping. Most people describe it as a unique but not unpleasant experience. You remain fully conscious and in control throughout."
    }, {
      question: 'How many EMDR sessions will I need?',
      answer: 'Research shows that many people experience significant relief in 6-12 sessions. Single-incident trauma may resolve in fewer sessions, while complex trauma may require more.'
    }, {
      question: 'Is EMDR scientifically proven?',
      answer: 'Yes. EMDR is recognized by the World Health Organization, the American Psychological Association, and many other international health organizations as an effective treatment for PTSD and trauma.'
    }, {
      question: 'Can EMDR help with issues other than trauma?',
      answer: 'Yes. While EMDR was originally developed for trauma, it has been shown to be effective for anxiety, phobias, grief, performance anxiety, and other concerns.'
    }]
  }
};
function FAQItem({
  question,
  answer
}: {
  question: string;
  answer: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border border-charcoal/10 rounded-xl bg-white overflow-hidden" data-id="element-1032">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full flex items-center justify-between p-5 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-olive focus-visible:ring-inset" aria-expanded={isOpen} data-id="element-1033">
        <span className="font-bold text-charcoal text-lg" data-id="element-1034">{question}</span>
        <ChevronDown size={20} className={`text-olive transition-transform duration-300 flex-shrink-0 ml-4 ${isOpen ? 'rotate-180' : ''}`} data-id="element-1035" />
      </button>
      <div className={`transition-all duration-300 ease-in-out ${isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'} overflow-hidden`} data-id="element-1036">
        <p className="p-5 pt-0 text-charcoal/80 font-medium leading-relaxed" data-id="element-1037">
          {answer}
        </p>
      </div>
    </div>;
}
export function ServiceDetailPage() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const service = id ? SERVICE_DATA[id] : null;
  if (!service) {
    return <Navigate to="/services" replace data-id="element-1038" />;
  }
  const Icon = service.icon;
  const processParagraphs = service.longDescription.split('\n\n');
  return <>
      {/* Section 1: Hero */}
      <section className="relative min-h-[40vh] md:min-h-[50vh] flex items-center overflow-hidden" data-id="element-1039">
        <div className="absolute inset-0" data-id="element-1040">
          <img src={service.image} alt={service.title} className="w-full h-full object-cover" data-id="element-1041" />
          <div className="absolute inset-0 bg-gradient-to-r from-charcoal/90 via-charcoal/70 to-charcoal/40" data-id="element-1042" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-16" data-id="element-1043">
          <div data-id="element-1044">
            <Link to="/services" className="inline-flex items-center text-white/70 hover:text-white text-sm font-bold mb-6 transition-colors uppercase tracking-wide" data-id="element-1045">
              ← Back to Services
            </Link>
            <div className="flex items-center gap-4 mb-4" data-id="element-1046">
              <div className="w-16 h-16 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center flex-shrink-0" data-id="element-1047">
                <Icon size={32} className="text-white" strokeWidth={1.5} data-id="element-1048" />
              </div>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight" data-id="element-1049">
                {service.title}
              </h1>
            </div>
            <p className="text-white/80 text-xl font-medium max-w-2xl mt-4" data-id="element-1050">
              {service.tagline}
            </p>
          </div>
        </div>
      </section>

      {/* Section 1.5: Why this service */}
      <section className="py-16 lg:py-24 bg-olive/5" data-id="element-1051">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1052">
          <div className="bg-white rounded-3xl shadow-soft-lg overflow-hidden border border-charcoal/5" data-id="element-1053">
            <div className="grid lg:grid-cols-2" data-id="element-1054">
              <div className="p-8 md:p-12 lg:p-16 flex flex-col justify-center" data-id="element-1055">
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-charcoal mb-6 leading-tight" data-id="element-1056">
                  {service.customSection1Title || `Why ${service.title}?`}
                </h2>
                <div className="w-16 h-1 bg-gold mb-8 rounded-full" data-id="element-1057"></div>
                <p className="text-charcoal/80 text-lg leading-relaxed font-medium mb-8" data-id="element-1058">
                  {service.whyDescription}
                </p>
                <div className="mt-auto flex flex-col sm:flex-row items-start sm:items-center gap-6" data-id="element-1059">
                  <a href="https://esthermensah.janeapp.com/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center bg-olive hover:bg-olive/90 text-white px-6 py-3 rounded-full font-bold text-sm transition-all shadow-md min-h-[44px] uppercase tracking-wide" data-id="element-1060">
                    Book an Appointment
                  </a>
                  <div className="flex items-center text-olive font-bold text-sm uppercase tracking-wide" data-id="element-1061">
                    <ArrowRight size={16} className="mr-2 rotate-90" data-id="element-1062" />
                    Learn More Below
                  </div>
                </div>
              </div>
              <div className="aspect-square lg:aspect-auto h-full relative" data-id="element-1063">
                <img src={service.image} alt={`${service.title} preview`} className="w-full h-full object-cover" data-id="element-1064" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 2: Support Areas */}
      <section className="py-16 lg:py-24 bg-cream" data-id="element-1065">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1066">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center" data-id="element-1067">
            <div className={`order-2 lg:order-1 ${id === 'couples' ? 'aspect-[3/2]' : 'aspect-[4/3]'} rounded-2xl overflow-hidden shadow-soft-lg`} data-id="element-1068">
              <img src={service.customSection2Image || service.image} alt={`${service.title} support`} className={`w-full h-full object-cover ${id === 'couples' ? 'object-[center_20%]' : 'object-top'}`} data-id="element-1069" />
            </div>
            <div className="order-1 lg:order-2" data-id="element-1070">
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-charcoal mb-8 leading-tight" data-id="element-1071">
                {service.customSection2Title || `${service.title} can support you in...`}
              </h2>
              <div className="space-y-4 mb-8" data-id="element-1072">
                {service.supportAreas.map((item, index) => <div key={index} className="bg-white rounded-xl p-4 shadow-sm border border-charcoal/5 flex items-start gap-4" data-id="element-1073">
                    <div className="w-8 h-8 rounded-full bg-olive/10 flex items-center justify-center flex-shrink-0 text-olive font-bold text-sm" data-id="element-1074">
                      {index + 1}
                    </div>
                    <span className="text-base text-charcoal/80 font-medium pt-1" data-id="element-1075">
                      {item}
                    </span>
                  </div>)}
              </div>
              <a href="https://esthermensah.janeapp.com/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center bg-olive hover:bg-olive/90 text-white px-6 py-3 rounded-full font-bold text-sm transition-all shadow-md min-h-[44px] uppercase tracking-wide" data-id="element-1076">
                Book an Appointment
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Section 3: My Process */}
      <section className="py-16 lg:py-24 bg-white border-y border-charcoal/5" data-id="element-1077">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1078">
          <div className="text-center mb-16" data-id="element-1079">
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-charcoal" data-id="element-1080">
              {service.customSection3Title || 'My Process'}
            </h2>
          </div>
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-start" data-id="element-1081">
            <div className={`${id === 'family' ? 'lg:col-span-5 aspect-[4/3]' : 'lg:col-span-4 aspect-[4/5]'} rounded-2xl overflow-hidden shadow-soft-lg lg:sticky lg:top-32`} data-id="element-1082">
              <img src={service.customSection3Image || 'https://cdn.magicpatterns.com/uploads/jQe2s7q7jLVpQYpaXA9qXV/Process_Esther.png'} alt="Esther Mensah" className={`w-full h-full object-cover ${id === 'family' ? 'object-center' : 'object-top'}`} data-id="element-1083" />
            </div>
            <div className={`${id === 'family' ? 'lg:col-span-7' : 'lg:col-span-8'}`} data-id="element-1084">
              <div className="relative border-l-2 border-olive/20 pl-8 ml-4 space-y-12 mb-12" data-id="element-1085">
                {processParagraphs.map((paragraph, index) => <div key={index} className="relative" data-id="element-1086">
                    <div className="absolute -left-[41px] top-1 w-5 h-5 rounded-full bg-white border-4 border-olive" data-id="element-1087" />
                    <p className="text-charcoal/80 text-lg font-medium leading-relaxed" data-id="element-1088">
                      {paragraph}
                    </p>
                  </div>)}
              </div>

              <div className="bg-olive/5 rounded-2xl p-8 border border-olive/10 mb-8" data-id="element-1089">
                <h3 className="font-heading text-xl font-bold text-charcoal mb-4" data-id="element-1090">
                  {id === 'emdr' ? '8-Phase EMDR Protocol' : 'Therapeutic Approaches Used'}
                </h3>
                <div className="flex flex-wrap gap-3" data-id="element-1091">
                  {service.approaches.map(a => <span key={a} className="bg-white text-charcoal font-bold text-sm px-4 py-2 rounded-full border border-charcoal/10 shadow-sm" data-id="element-1092">
                      {a}
                    </span>)}
                </div>
              </div>

              <a href="https://esthermensah.janeapp.com/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center bg-olive hover:bg-olive/90 text-white px-6 py-3 rounded-full font-bold text-sm transition-all shadow-md min-h-[44px] uppercase tracking-wide" data-id="element-1093">
                Book an Appointment
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Section 4: FAQs */}
      <section className="py-16 lg:py-24 bg-cream" data-id="element-1094">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1095">
          <div className="text-center mb-12" data-id="element-1096">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-charcoal mb-4" data-id="element-1097">
              Frequently Asked Questions
            </h2>
            <p className="text-charcoal/70 text-lg font-medium" data-id="element-1098">
              Common questions about {service.title.toLowerCase()}
            </p>
          </div>
          <div className="space-y-4" data-id="element-1099">
            {service.faqs.map((faq, index) => <FAQItem key={index} question={faq.question} answer={faq.answer} data-id="element-1100" />)}
          </div>
        </div>
      </section>

      {/* Section 5: CTA */}
      <section className="py-20 lg:py-24 bg-olive text-center" data-id="element-1101">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1102">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-1103">
            Ready to Get Started?
          </h2>
          <p className="text-white/80 text-lg font-medium mb-10 max-w-2xl mx-auto" data-id="element-1104">
            Take the first step toward healing. Book a free 15-minute
            consultation to discuss how {service.title.toLowerCase()} can help
            you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1105">
            <a href="https://esthermensah.janeapp.com/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center bg-gold hover:bg-gold/90 text-white px-8 py-4 rounded-full font-bold text-base transition-all shadow-lg min-h-[52px] tracking-wide uppercase" data-id="element-1106">
              <Calendar size={18} className="mr-2" data-id="element-1107" />
              Book Appointment
            </a>
            <Link to="/contact" className="inline-flex items-center justify-center bg-white/10 hover:bg-white/20 text-white border border-white/30 px-8 py-4 rounded-full font-bold text-base transition-all min-h-[52px] tracking-wide uppercase" data-id="element-1108">
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </>;
}